import React, { useState, useEffect, useRef, useCallback } from 'react';
import './CenterPanel.css';
import NFT_TEMPLATES from '../config/nftTemplates';
import WORKER_TEMPLATES from '../config/workerTemplates';
import { WORKER_PACK_TEMPLATES, MAP_WORKER_PACKS } from '../config/workerPackTemplates';
import PACK_TEMPLATES from '../config/packTemplates';
import {
  actClaimNFT, actRepairNFT, actClaimWorker, actCraftButton,
  actHealAsset,
  actStakeNFT, actUnstakeNFT, actWithdraw, actDeposit,
  actSetAssistance, getConfigs, getTokenBalance,
  getnetworkTable, actSetMyref, getNetworkEarnings, actVote
} from '../api/waxApi';
import { getAtomicInventory } from '../api/waxApi';
import SEASONS from '../config/seasons';
import HealModal from './HealModal';
import { useNotifications } from '../contexts/NotificationContext';

import homeIcon from '../assets/icons/home.webp';
import chestIcon from '../assets/icons/chest.webp';
import smithIcon from '../assets/icons/craft.webp';
import exchangeIcon from '../assets/icons/exchange.webp';
import inventoryIcon from '../assets/icons/inventory.webp';
import settingIcon from '../assets/icons/settingIcon.webp';
import networkIcon from '../assets/icons/networkIcon.webp';

import ranchIcon from '../assets/icons/ranch.webp';
import farmIcon from '../assets/icons/farm.webp';
import toolIcon from '../assets/icons/tool.webp';
import rcoinIcon from '../assets/icons/rcoin.webp';
import staminaIcon from '../assets/icons/stamina.webp';
import questionIcon from '../assets/icons/question_icon.webp'; // en üste ekle

const TYPE_ICONS = {
  Farm: farmIcon,
  Ranch: ranchIcon,
  Tool: toolIcon,
};

const TOKEN_ICONS = {
  RANCH: ranchIcon,
  FARM: farmIcon,
  TOOL: toolIcon,
  RCOIN: rcoinIcon,
  STAMINA: staminaIcon
};

export default function CenterPanel({
  selected, nfts, workers, playerHealData, tab, setTab, balances,
  tokenBalances, setTokenBalances, inventory, setUserInventory,
  wallet, onRefresh, refreshNFT, refreshWorker, refreshFinance, refreshUser,
  refreshHealData, setLogLines, setLogModalOpen, assistance, setErrorModalOpen,
  setErrorMessage, stamina, isProcessing, setIsProcessing, seasonId, onOpenRepairModal, 
  notificationModalOpen, setNotificationModalOpen }) {

  // Use notification context
  const { 
    hasUnreadNotifications, 
    markNotificationsAsViewed, 
  } = useNotifications();

  const [sel, setSel] = useState(null);
  const [selcraft, setSelcraft] = useState(null);
  const [exchangeTab, setExchangeTab] = useState('withdraw');
  const [inputValues, setInputValues] = useState({
    ranch: '',
    farm: '',
    tool: '',
    rcoin: ''
  });
  const [remainingTime, setRemainingTime] = useState(0);
  const chestScrollRef = useRef(null);
  const [fee, setFee] = useState(0);
  const [copyStatus, setCopyStatus] = useState('Copy');
  const [refInput, setRefInput] = useState('');
  const [referrerAccount, setReferrerAccount] = useState('');
  const [hasReferrer, setHasReferrer] = useState(false);
  const [networkData, setNetworkData] = useState({ referrer: '', invites: [] });

  const [networkEarnings, setNetworkEarnings] = useState([]);
  const [networkStack, setNetworkStack] = useState([wallet]);
  const currentNetworkUser = networkStack.length > 1
    ? networkStack[networkStack.length - 1]
    : null;

  // ✅ Add heal modal state variables
  const [healCost, setHealCost] = useState(null);
  const [healModalOpen, setHealModalOpen] = useState(false);

  // Notification handler
  const handleNotificationClick = () => {
    setNotificationModalOpen(true);
    markNotificationsAsViewed();
  };



  const fetchFee = async () => {
    const config = await getConfigs();
    if (config?.fee !== undefined) {
      setFee(config.fee);
    }
  };

  const fetchNetwork = useCallback(async (acct) => {
    try {
      const result = await getnetworkTable(acct);
      if (result) {
        const { referrer = '', invites = [] } = result;
        setNetworkData({ referrer, invites });
      } else {
        // If no data returned, set empty defaults
        setNetworkData({ referrer: '', invites: [] });
      }
    } catch (error) {
      console.error('Error fetching network data:', error);
      setNetworkData({ referrer: '', invites: [] });
    }
  }, []);

  const fetchEarnings = useCallback(async (acct) => {
    // sadece kendi scope’unuzdan çekiyor, dolayısıyla acct bizim davetlimiz olduğunda data var
    const row = await getNetworkEarnings(acct);
    if (row?.earn && Array.isArray(row.earn)) {
      const parsed = row.earn.map(e => {
        const [amt, token] = e.split(' ');
        return { token, amount: parseFloat(amt) || 0 };
      });
      setNetworkEarnings(parsed);
    } else {
      setNetworkEarnings([]);
    }
  }, []);

  const fetchOutBalance = async () => {
    const tokenSymbols = ["RANCH", "FARM", "TOOL", "RCOIN"];
    const results = await Promise.all(
      tokenSymbols.map(symbol => getTokenBalance("ranchersbank", wallet, symbol))
    );
    setTokenBalances(results);
  };

  const fetchRef = async () => {
    const result = await getnetworkTable(wallet);
    if (result?.referrer && result.referrer !== '') {
      setReferrerAccount(result.referrer);
      setHasReferrer(true);
    } else {
      setReferrerAccount('');
      setHasReferrer(false);
    }
  };

  const getHealCosts = async (template) => {
    const workerPack = MAP_WORKER_PACKS.map(c => c.workers?.includes(template) ? c : null).find(c => c !== null);
    if (workerPack == null || !workerPack.pack)
      return await getNFTHealCosts(template);
    else {
      return await getWorkerHealCosts(workerPack.pack);
    }
  };

  const getNFTHealCosts = async (template) => {
    const nftTemplate = NFT_TEMPLATES.find(nt => nt.template_id === template);
    if (!nftTemplate?.mints) return null;

    const cost = nftTemplate.mints.find(m => m.includes('RANCH'));
    if (!cost) return null;

    const [amount] = cost.split(' ');
    return { amount: parseFloat(amount) / 2, token: 'RANCH' };
  };

  const getWorkerHealCosts = async (template) => {
    const packTemplate = WORKER_PACK_TEMPLATES.find(wt => wt.template_id === template);
    if (!packTemplate?.mint_cost) return null;

    const { first, second } = packTemplate.mint_cost;
    const rcoinCost = first?.includes('RCOIN') ? first : second?.includes('RCOIN') ? second : null;
    if (!rcoinCost) return null;

    const [amount] = rcoinCost.split(' ');
    return { amount: parseFloat(amount) / 2, token: 'RCOIN' };
  };

  useEffect(() => {
    if ((tab === 'chest' || tab === 'inventory') && chestScrollRef.current) {
      chestScrollRef.current.scrollLeft = 0;
    }
    else if (tab === 'home') {
      if (nfts.length > 0) {
        setSel(nfts[0]);
      } else if (workers.length > 0) {
        setSel(workers[0]);
      } else {
        setSel(null);
      }
    }
    else if (tab === 'smith') {
      if (NFT_TEMPLATES.length > 0) {
        setSelcraft({ template_id: NFT_TEMPLATES[0].template_id });
      }
    }
    else if (tab === 'exchange') {
      fetchFee();
      fetchOutBalance();
    }
    else if (tab === 'settings') {
      fetchRef();
    }
  }, [tab]);

  useEffect(() => {
    if (tab === 'network') {
      setNetworkStack([wallet]);
    }
  }, [tab, wallet]);

  useEffect(() => {
    if (tab === 'network') {
      const acct = currentNetworkUser || wallet;

      fetchNetwork(acct);
      fetchEarnings(acct);
    }
  }, [tab, currentNetworkUser, wallet, fetchNetwork, fetchEarnings]);

  useEffect(() => {
    if (tab === 'home') {
      // Eğer seçili olan mevcut listede varsa onu tut
      const allNfts = [...nfts, ...workers];
      if (sel) {
        const match = allNfts.find(n => n.asset_id === sel.asset_id);
        if (match) {
          setSel(null);
          setTimeout(() => setSel(match), 0);
          return;
        }
      }
      // Yoksa default olarak ilkini seç
      if (nfts.length > 0) {
        setSel(nfts[0]);
      } else if (workers.length > 0) {
        setSel(workers[0]);
      } else {
        setSel(null);
      }
    }
  }, [nfts, workers]);

  useEffect(() => {
    const updateTime = () => {
      if (!sel || !sel.next_claim) return;

      const isWorker = WORKER_TEMPLATES.some(t => t.template_id === sel.template_id);

      const template = isWorker
        ? WORKER_TEMPLATES.find(t => t.template_id === sel.template_id)
        : NFT_TEMPLATES.find(t => t.template_id === sel.template_id);

      if (!template) return;

      const currentSeason = SEASONS.find(s => s.id === seasonId);
      const chargedTime = currentSeason
        ? (template.charged_time * currentSeason.cooldown_multiplier) / 100
        : template.charged_time;

      const now = Math.floor(Date.now() / 1000);
      const next = sel.next_claim;

      let totalClaimable = 1;

      if (!isWorker) {
        const matchedWorkers = workers.filter(w => {
          const t = WORKER_TEMPLATES.find(x => x.template_id === w.template_id);
          return t?.type === template.type;
        });

        const extraClaims = matchedWorkers.reduce((sum, w) => {
          const wt = WORKER_TEMPLATES.find(t => t.template_id === w.template_id);
          return sum + (wt?.saved_claims || 0);
        }, 0);

        totalClaimable += extraClaims;
      }

      let current = 0;
      if (now >= next) {
        const diff = now - next;
        const fullCharges = Math.floor(diff / chargedTime);
        current = Math.min(1 + fullCharges, totalClaimable);
      }

      const remaining = current >= totalClaimable ? 0 : (next + current * chargedTime - now);
      setRemainingTime(remaining);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, [sel, seasonId, workers]);



  useEffect(() => {
    if (selected) {
      setSel(selected);
    } else if (nfts.length > 0) {
      setSel(nfts[0]);
    }
  }, [selected, nfts]);


  useEffect(() => {
    if (selected) {
      setSel(selected);
    }
  }, [selected]);

  const ranchInputRef = useRef(null);
  const farmInputRef = useRef(null);
  const toolInputRef = useRef(null);
  const rcoinInputRef = useRef(null);

  useEffect(() => {
    if (ranchInputRef.current) ranchInputRef.current.value = "";
    if (farmInputRef.current) farmInputRef.current.value = "";
    if (toolInputRef.current) toolInputRef.current.value = "";
    if (rcoinInputRef.current) rcoinInputRef.current.value = "";
    setInputValues({ ranch: '', farm: '', tool: '', rcoin: '' });
  }, [exchangeTab]);

  useEffect(() => {
    const viewer = localStorage.getItem("loggedInUser");
    if (viewer !== wallet && tab !== 'home') {
      setTab('home');
    }
  }, [wallet, tab]);

  const buildAssetList = () => {
    const precision = 4;
    const symbolMap = {
      ranch: 'RANCH',
      farm: 'FARM',
      tool: 'TOOL',
      rcoin: 'RCOIN'
    };

    const assets = [];

    Object.entries(inputValues).forEach(([key, val]) => {
      const num = parseFloat(val);
      if (!isNaN(num) && num > 0) {
        const formatted = `${num.toFixed(precision)} ${symbolMap[key]}`;
        assets.push(formatted);
      }
    });

    return assets;
  };

  const refreshInventory = useCallback(async () => {
    await new Promise((res) => setTimeout(res, 1000));
    const data = await getAtomicInventory(wallet);
    if (data) {
      setUserInventory(data);
    } else {
      setUserInventory([]);
    }
  }, [wallet, setUserInventory]);

  useEffect(() => {
    if (tab === 'inventory') {
      refreshInventory();
    }
  }, [tab, refreshInventory]);

  function handleInviteClick(inv) {
    setNetworkStack(prev => [...prev, inv]);
  }

  function handleBack() {
    setNetworkStack(prev => {
      if (prev.length <= 1) return prev;
      return prev.slice(0, -1);
    });
  }

  const handleInput = (e, maxBalance, key) => {
    let value = e.target.value;
    value = value.replace(/\s/g, '');

    if (value.includes('.') && value.includes(',')) {
      if (value.indexOf('.') < value.indexOf(',')) {
        value = value.replace(/,/g, '');
      } else {
        value = value.replace(/\./g, '');
      }
    }

    value = value.replace(',', '.');
    value = value.replace(/[^0-9.]/g, '');

    const parts = value.split('.');

    // Noktadan önce en fazla bir sıfıra izin ver
    if (parts[0]) {
      if (parts[0].length > 1 && parts[0].startsWith('0')) {
        parts[0] = '0';
      }
    }

    // Ondalık kısmı 4 hane ile sınırla
    if (parts[1]?.length > 4) {
      parts[1] = parts[1].slice(0, 4);
    }

    value = parts.join('.');

    if (value.startsWith('-')) {
      value = value.slice(1);
    }

    const numericValue = parseFloat(value) || 0;
    if (numericValue > maxBalance) {
      value = maxBalance.toFixed(4);
    }

    e.target.value = value;

    setInputValues(prev => ({
      ...prev,
      [key]: value
    }));
  };



  const handleKeyDown = (e) => {
    if (e.key === ' ') {
      e.preventDefault();
    }
  };

  const handleBlur = (key) => {
    setInputValues(prev => {
      const val = parseFloat(prev[key]);
      if (isNaN(val) || val <= 0) {
        return { ...prev, [key]: '' };
      }
      return prev;
    });
  };


  const renderDurabilityBar = (current, max) => {
    const ratio = Math.max(0, Math.min(1, current / max));
    return (
      <div className="durability-bar">
        <div className="durability-fill" style={{ width: `${ratio * 100}%` }}></div>
        <div className="durability-text">{current} / {max}</div>
      </div>
    );
  };

  // ✅ Get heal button color class based on current decay (not real-time calculated)
  const getHealButtonColorClass = (asset) => {
    const currentDecay = asset?.decay_data?.decay_hr || 336;
    const maxDecay = 336;
    const decayPercent = ((maxDecay - currentDecay) / maxDecay) * 100;

    if (decayPercent <= 25) {
      return 'heal-button-green'; // Good health (75-100% remaining)
    } else if (decayPercent <= 60) {
      return 'heal-button-orange'; // Medium health (40-75% remaining)
    } else {
      return 'heal-button-red'; // Poor health (0-40% remaining)
    }
  };

  // ✅ Calculate real-time decay based on elapsed time after claim availability
  const calculateRealTimeDecay = (asset, isWorker, currentSeason) => {
    const maxDecay = 336;
    const baseDecay = asset?.decay_data?.decay_hr || maxDecay;
    const now = Math.floor(Date.now() / 1000);
    const nextClaim = asset?.next_claim || 0;

    let debugObject = {};
    debugObject.asset_id = asset.asset_id;
    debugObject.currentTime = now;
    debugObject.nextClaimTime = nextClaim;
    debugObject.baseDecay = baseDecay;
    
    // If asset is not ready to claim yet, no additional decay
    if (now < nextClaim) {
      return {
        current: baseDecay,
        max: maxDecay,
        percent: Math.round(((maxDecay - baseDecay) / maxDecay) * 10000) / 100,
        hoursDecayed: 0
      };
    }
    let totalHoursDecayed = 0;

    if (isWorker) {
      // For workers: decay starts 1hr after the worker is claimable
      const hoursPassed = Math.floor((now - (nextClaim + 3600)) / 3600);
      totalHoursDecayed = hoursPassed;
    } else {
      // For NFTs: decay starts after all saved claims are exhausted
      const template = NFT_TEMPLATES.find(t => t.template_id === asset.template_id);
      if (template) {
        const baseTime = template.charged_time || 3600;
        const chargedTime = currentSeason ? (baseTime * currentSeason.cooldown_multiplier) / 100 : baseTime;

        debugObject.currentSeason = currentSeason ? currentSeason.id : null;
        debugObject.multipplier = currentSeason ? currentSeason.cooldown_multiplier : 0;

        debugObject.chargedTime = chargedTime;
        debugObject.baseChargedTime = baseTime;

        // Find matching workers and calculate extra claims
        const matchedWorkers = workers.filter(w => {
          const wTemp = WORKER_TEMPLATES.find(t => t.template_id === w.template_id);
          return wTemp?.type === template.type;
        });
        
        const extraClaims = matchedWorkers.reduce((sum, w) => {
          const workerTemplate = WORKER_TEMPLATES.find(t => t.template_id === w.template_id);
          return sum + (workerTemplate?.saved_claims || 0);
        }, 0);

        debugObject.extraClaims = extraClaims;
        // Total claimable includes base + extra claims
        const totalClaimable = 1 + extraClaims;
        
        // Calculate when all saved claims are exhausted
        const allClaimsExhaustedTime = nextClaim + ((totalClaimable - 1) * chargedTime) + 3600;
        debugObject.allClaimsExhaustedTime = allClaimsExhaustedTime;

        if (now > allClaimsExhaustedTime) {
          const hoursPassed = Math.floor((now - allClaimsExhaustedTime) / 3600);
          totalHoursDecayed = hoursPassed;
        }

        debugObject.totalHoursDecayed = totalHoursDecayed;
      }
    }

    // Calculate current decay (can't go below 0)
    const currentDecay = Math.max(0, baseDecay - totalHoursDecayed);
    const tempDecay = baseDecay - totalHoursDecayed;
    const decayPercent = ((maxDecay - currentDecay) / maxDecay) * 100;

    debugObject.currentDecay = currentDecay;
    // console.log("Decay Calculation Debug:", debugObject);
    
    return {
      current: currentDecay,
      max: maxDecay,
      percent: Math.round(decayPercent * 10000) / 100,
      hoursDecayed: Math.min(totalHoursDecayed, 336),
      temp: tempDecay
    };
  };

  // ✅ Calculate healer effect on selected asset based on contract logic
  const calculateHealerEffect = (selectedAsset, healerData) => {
    if (!selectedAsset || !healerData) {
      return null;
    }
    const nftHistory = playerHealData.nft_history || [];
    const alreadyUsed = nftHistory.includes(selectedAsset.asset_id?.toString());
    
    if (alreadyUsed) {
      return {
        canUse: false,
        reason: 'Healer already used on this NFT',
        currentDecay: 0,
        healedDecay: 0,
        healHours: 0
      };
    }

    const isWorker = WORKER_TEMPLATES.some(t => t.template_id === selectedAsset.template_id);
    const currentSeason = SEASONS.find(s => s.id === seasonId);
    const decayInfo = calculateRealTimeDecay(selectedAsset, isWorker, currentSeason);
    const tempDecay = decayInfo.temp;

    if (tempDecay >= 336) {
      return {
        canUse: false,
        reason: 'Asset is at full health (336)',
        currentDecay: tempDecay,
        healedDecay: tempDecay,
        healHours: 0
      };
    }

    const healTemplate = playerHealData.template_id;
    const healAmount = NFT_TEMPLATES.find(nt => nt.template_id === healTemplate)?.heal_amount || 0;

    let fixedHealAmount = 336 * healAmount / 100 + tempDecay;
    let healHours = fixedHealAmount <= 0 ? 0 : Math.min(fixedHealAmount, 336);

    console.log("Healer Effect Calculation Debug:", {
      asset_id: selectedAsset.asset_id,
      currentDecay: tempDecay,
      healTemplate: healTemplate,
      healAmount: healAmount,
      healHours: healHours
    });

    const healedDecay = Math.min(336, healHours);
    return {
      canUse: true,
      reason: `Will heal ${healHours} hours`,
      currentDecay: tempDecay,
      healedDecay: healedDecay,
      healHours: healHours,
      improvement: healedDecay - tempDecay
    };
  };

  const isHealerAvailable = () => {
    const hasHealer = playerHealData && playerHealData.template_id && playerHealData.template_id !== 0;
    console.log("Healer availability check:", {
      playerHealData,
      template_id: playerHealData?.template_id,
      hasHealer
    });
    return hasHealer;
  };

  function formatNumber(n) {
    if (n >= 1000000) return (n / 1000000).toFixed(2) + 'M';
    if (n >= 1000) return (n / 1000).toFixed(2) + 'K';
    return n.toFixed(4);
  }

  const handleMine = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      const viewer = localStorage.getItem("loggedInUser");
      const target = wallet;

      if (!viewer || !sel?.asset_id) {
        setIsProcessing(false);
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 50));
      const tx = await actClaimNFT(target, sel.asset_id);
      if (tx?.error) {
        setErrorMessage(tx.error);
        setErrorModalOpen(true);
        return;
      }

      if (tx?.log) {
        const rawLog = Array.isArray(tx.log) ? tx.log : [tx.log];

        const formattedLog = rawLog.flatMap((line) => {
          if (typeof line !== 'string') return [line];
          const parts = line.match(/(Reward:[^B\n\r]*)|(Bonus:[^\n\r]*)|[^]*?(?=Reward:|Bonus:|$)/g)
            ?.map(s => s.trim())
            .filter(Boolean);

          return parts?.length ? parts : [line];
        });

        setLogLines(formattedLog);
        setLogModalOpen(true);
      }

      if (refreshNFT && sel?.asset_id) {
        await refreshNFT(sel.asset_id);
      }
      
      if (refreshUser) {
        await refreshUser();
        await refreshHealData();
      }
    } catch (error) {
      console.error('Error in handleMine:', error);
      setErrorMessage('An error occurred while claiming NFT');
      setErrorModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRepair = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      const viewer = localStorage.getItem("loggedInUser");
      const target = wallet;

      if (!viewer || !sel?.asset_id) {
        setIsProcessing(false);
        return;
      }

      await new Promise(resolve => setTimeout(resolve, 50));
      const tx = await actRepairNFT(target, sel.asset_id);
      
      if (tx?.error) {
        setErrorMessage(tx.error);
        setErrorModalOpen(true);
        return;
      }

      if (tx?.log) {
        setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
        setLogModalOpen(true);
      }
      
      if (refreshNFT && sel?.asset_id) {
        await refreshNFT(sel.asset_id);
      }
      
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error in handleRepair:', error);
      setErrorMessage('An error occurred while repairing NFT');
      setErrorModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleHeal = async () => {
    if (isProcessing) return;
    setIsProcessing(true);

    const viewer = localStorage.getItem("loggedInUser");
    const target = wallet;

    if (!viewer || !sel?.asset_id) {
      setIsProcessing(false);
      return;
    }

    const tx = await actHealAsset(target, sel.asset_id);

    if (tx?.error) {
      setErrorMessage(tx.error);
      setErrorModalOpen(true);
      setIsProcessing(false);
      return;
    }

    if (tx?.log) {
      setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
      setLogModalOpen(true);
    }

    // Close heal modal and refresh NFT data
    setHealModalOpen(false);
    setHealCost(null);

    if (refreshNFT && sel?.asset_id && sel?.template_id){
      const isWorker = WORKER_TEMPLATES.some(t => t.template_id === sel.template_id);
      if(isWorker)
        await refreshWorker(sel.asset_id);
      else
        await refreshNFT(sel.asset_id);
    }
    setIsProcessing(false);
  };

  // ✅ Handle heal modal close
  const handleHealModalClose = () => {
    setHealModalOpen(false);
    setHealCost(null);
  };

  // ✅ Handle heal confirmation from modal
  const handleHealConfirm = async () => {
    await handleHeal();
  };

  const handleRepairChest = async (asset_id) => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      const target = wallet; // URL'den gelen wallet (başkasının sayfası olabilir)

      const tx = await actRepairNFT(target, asset_id);
      if (tx?.error) {
        setErrorMessage(tx.error);
        setErrorModalOpen(true);
        return;
      }

      if (tx?.log) {
        setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
        setLogModalOpen(true);
      }
      
      // Refresh NFT data after successful repair
      if (refreshNFT) {
        await refreshNFT(asset_id);
      }
      
      // Also refresh user data to update balances
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error in handleRepairChest:', error);
      setErrorMessage('An error occurred while repairing chest');
      setErrorModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCopyReferral = () => {
    const refLink = `https://www.ranchers.land/play/?ref=${wallet}`;
    navigator.clipboard.writeText(refLink);
    setCopyStatus('Copied!');
    setTimeout(() => setCopyStatus('Copy'), 2000);
  };

  const handleClaimWorker = async () => {
    if (isProcessing) return;
    
    // Set processing state immediately for instant feedback
    setIsProcessing(true);

    try {
      const viewer = localStorage.getItem("loggedInUser"); // aksiyonu atan kişi
      const target = wallet; // URL'den gelen wallet (başkasının sayfası olabilir)

      if (!viewer || !sel?.asset_id) {
        setIsProcessing(false);
        return;
      }

      // Small delay to ensure UI updates before blockchain call
      await new Promise(resolve => setTimeout(resolve, 50));
      
      const tx = await actClaimWorker(target, sel.asset_id);

      if (tx?.error) {
        setErrorMessage(tx.error);
        setErrorModalOpen(true);
        return;
      }

      if (tx?.log) {
        setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
        setLogModalOpen(true);
      }
      
      // Refresh the worker data after successful claim
      if (refreshWorker && sel?.asset_id) {
        await refreshWorker(sel.asset_id);
      }
      
      // Also refresh user data to update balances
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error in handleClaimWorker:', error);
      setErrorMessage('An error occurred while claiming worker');
      setErrorModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCraft = async () => {
    if (isProcessing) return;
    
    // Set processing state immediately for instant feedback
    setIsProcessing(true);

    try {
      const viewer = localStorage.getItem("loggedInUser"); // aksiyonu atan kişi

      if (!viewer || !selcraft?.template_id) {
        setIsProcessing(false);
        return;
      }

      // Small delay to ensure UI updates before blockchain call
      await new Promise(resolve => setTimeout(resolve, 50));

      const tx = await actCraftButton(selcraft.template_id);

      if (tx?.error) {
        setErrorMessage(tx.error);
        setErrorModalOpen(true);
        return;
      }

      if (tx?.log) {
        setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
        setLogModalOpen(true);
      }
      
      // Refresh user data after successful craft
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error in handleCraft:', error);
      setErrorMessage('An error occurred while crafting');
      setErrorModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStake = async (asset_id) => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      const viewer = localStorage.getItem("loggedInUser");
      if (!viewer || !asset_id) {
        return;
      }

      const tx = await actStakeNFT(asset_id);

      if (tx?.error) {
        setErrorMessage(tx.error);
        setErrorModalOpen(true);
        return;
      }

      if (tx?.log) {
        setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
        setLogModalOpen(true);
      }

      // Refresh inventory if on inventory tab
      if (tab === 'inventory') {
        await refreshInventory();
      }

      // Refresh overall data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error in handleStake:', error);
      setErrorMessage('An error occurred while staking NFT');
      setErrorModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUnstake = async (asset_id) => {
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      const viewer = localStorage.getItem("loggedInUser");
      if (!viewer || !asset_id) {
        return;
      }

      const tx = await actUnstakeNFT(asset_id);

      if (tx?.error) {
        setErrorMessage(tx.error);
        setErrorModalOpen(true);
        return;
      }

      if (tx?.log) {
        setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
        setLogModalOpen(true);
      }

      // Refresh inventory if on inventory tab
      if (tab === 'inventory') {
        await refreshInventory();
      }

      // Refresh overall data
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error in handleUnstake:', error);
      setErrorMessage('An error occurred while unstaking NFT');
      setErrorModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleWithdraw = async () => {
    if (isProcessing) return;
    
    // Set processing state immediately for instant feedback
    setIsProcessing(true);

    try {
      const assets = buildAssetList();

      // Small delay to ensure UI updates before blockchain call
      await new Promise(resolve => setTimeout(resolve, 50));

      const tx = await actWithdraw(assets);
      
      if (tx?.error) {
        setErrorMessage(tx.error);
        setErrorModalOpen(true);
        
        if (refreshFinance) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait longer for blockchain
          await refreshFinance();
        }
        return;
      }

      if (tx?.log) {
        setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
        setLogModalOpen(true);
      }

      // Clear input values after successful withdraw
      setInputValues({ ranch: '', farm: '', tool: '', rcoin: '' });
      if (ranchInputRef.current) ranchInputRef.current.value = "";
      if (farmInputRef.current) farmInputRef.current.value = "";
      if (toolInputRef.current) toolInputRef.current.value = "";
      if (rcoinInputRef.current) rcoinInputRef.current.value = "";

      // Add delay for blockchain propagation before refreshing
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Refresh finance data to update token balances
      if (refreshFinance) {
        await refreshFinance();
      }
      
      // Also refresh user data to ensure all balances are updated
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error in handleWithdraw:', error);
      setErrorMessage('An error occurred while withdrawing');
      setErrorModalOpen(true);
      
      if (refreshFinance) {
        try {
          await new Promise(resolve => setTimeout(resolve, 1000));
          await refreshFinance();
        } catch (refreshError) {
          console.error('Error refreshing after withdraw error:', refreshError);
        }
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeposit = async () => {
    if (isProcessing) return;
    
    // Set processing state immediately for instant feedback
    setIsProcessing(true);

    try {
      const assets = buildAssetList();

      // Small delay to ensure UI updates before blockchain call
      await new Promise(resolve => setTimeout(resolve, 50));

      const tx = await actDeposit(assets);
      
      if (tx?.error) {
        setErrorMessage(tx.error);
        setErrorModalOpen(true);
        if (refreshFinance) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait longer for blockchain
          await refreshFinance();
        }
        return;
      }

      if (tx?.log) {
        setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
        setLogModalOpen(true);
      }

      // Clear input values after successful deposit
      setInputValues({ ranch: '', farm: '', tool: '', rcoin: '' });
      if (ranchInputRef.current) ranchInputRef.current.value = "";
      if (farmInputRef.current) farmInputRef.current.value = "";
      if (toolInputRef.current) toolInputRef.current.value = "";
      if (rcoinInputRef.current) rcoinInputRef.current.value = "";

      // Add delay for blockchain propagation before refreshing
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Refresh finance data to update token balances
      if (refreshFinance) {
        await refreshFinance();
      }
      
      // Also refresh user data to ensure all balances are updated
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      console.error('Error in handleDeposit:', error);
      setErrorMessage('An error occurred while depositing');
      setErrorModalOpen(true);
      
      if (refreshFinance) {
        try {
          await new Promise(resolve => setTimeout(resolve, 1000));
          await refreshFinance();
        } catch (refreshError) {
          console.error('Error refreshing after deposit error:', refreshError);
        }
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVoteProxy = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const tx = await actVote(); // proxy adresi backend'den ayarlanmalı
      if (tx?.error) {
        setErrorMessage(tx.error);
        setErrorModalOpen(true);
        return;
      }
      if (tx?.log) {
        setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
        setLogModalOpen(true);
      }
    } catch (err) {
      setErrorMessage(err.message || 'Unknown error during voting.');
      setErrorModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderLeftList = () => {
    const allNfts = [...nfts, ...workers];

    const hasTool = nfts.some(n => NFT_TEMPLATES.some(t => t.template_id === n.template_id));
    const hasNfts = allNfts.length > 0;

    const renderNoNftsMessage = (message) => (
      <div className="no-tool-message">
        <p>{message}</p>
        <div className="no-tool-buttons">
          <button onClick={() => window.open('/market', '_blank')}>
            Buy NFTs
          </button>
          <button onClick={() => window.open('https://alcor.exchange/markets?search=ranchersbank', '_blank')}>
            Buy Tokens
          </button>
        </div>
      </div>
    );

    if (tab === 'home') {
      if (!hasTool && workers.length === 0) {
        return renderNoNftsMessage("You do not own any production NFTs.\nCraft with tokens or buy from the secondary market!");
      }
      return (
        <div className="left-scroll-list">
          {nfts.map((n) => {
            const template = NFT_TEMPLATES.find(t => t.template_id === n.template_id);
            if (!template) return null;
            const isSelected = sel?.asset_id === n.asset_id;
            return (
              <img
                key={`nft-${n.asset_id}`}
                className={`nft-thumb ${isSelected ? 'active' : 'faded'}`}
                src={template.img}
                onClick={() => setSel(n)}
                alt={template.nft_name || ''}
              />
            );
          })}
          {workers.map((w) => {
            const template = WORKER_TEMPLATES.find(t => t.template_id === w.template_id);
            if (!template) return null;
            const isSelected = sel?.asset_id === w.asset_id;
            return (
              <img
                key={`worker-${w.asset_id}`}
                className={`nft-thumb ${isSelected ? 'active' : 'faded'}`}
                src={template.img}
                onClick={() => setSel(w)}
                alt={template.worker_name || ''}
              />
            );
          })}
          {(() => {
              if (!playerHealData?.template_id || playerHealData.template_id === 0) return null;
              const template = NFT_TEMPLATES.find(t => t.template_id === playerHealData.template_id) ||
                              WORKER_TEMPLATES.find(t => t.template_id === playerHealData.template_id);
              if (!template) return null;
              return (
                <img
                  key={`heal-${playerHealData.asset_id}`}
                  className={`nft-thumb ${sel?.asset_id === playerHealData.asset_id ? 'active' : 'faded'}`}
                  src={template.img}
                  onClick={() => setSel(playerHealData)}
                  alt={template.nft_name || template.worker_name || ''}
                />
              );
          })()}
        </div>
      );
    }

    else if (tab === 'chest') {
      if (!hasNfts) {
        return renderNoNftsMessage("You do not own any NFTs.\nCraft or buy NFTs to start playing!");
      }

      const currentSeason = SEASONS.find(s => s.id === seasonId);

      return (
        <div className="chest-scroll-list" ref={chestScrollRef}>
          {allNfts.map((n, index) => {
            const isWorker = WORKER_TEMPLATES.some(w => w.template_id === n.template_id);
            const template =
              NFT_TEMPLATES.find(t => t.template_id === n.template_id) ||
              WORKER_TEMPLATES.find(w => w.template_id === n.template_id);

            if (!template) return null;

            const now = Math.floor(Date.now() / 1000);
            const joinTime = n.join_time || 0;
            const nextClaim = n.next_claim || 0;
            const currentDurability = n.durability?.first || 0;
            const maxDurability = n.durability?.second || 100;

            const isFullDurability = currentDurability >= maxDurability;
            let buttonLabel = "Remove";
            let buttonDisabled = false;
            let displayText = '';

            if (isWorker) {
              const lockRemaining = (joinTime + 259200) - now;

              if (lockRemaining > 0) {
                const hours = Math.floor(lockRemaining / 3600);
                const minutes = Math.floor((lockRemaining % 3600) / 60);
                const seconds = lockRemaining % 60;
                displayText = `${hours.toString().padStart(2, '0')}:${minutes
                  .toString()
                  .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                buttonDisabled = true;
              } else if (nextClaim > now) {
                const claimWait = nextClaim - now;
                const hours = Math.floor(claimWait / 3600);
                const minutes = Math.floor((claimWait % 3600) / 60);
                const seconds = claimWait % 60;
                displayText = `${hours.toString().padStart(2, '0')}:${minutes
                  .toString()
                  .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                buttonDisabled = true;
              }
            } else {
              if (!isFullDurability) {
                displayText = `${currentDurability} / ${maxDurability}`;
                buttonLabel = "Repair";
              } else if (nextClaim > now) {
                const claimWait = nextClaim - now;
                const hours = Math.floor(claimWait / 3600);
                const minutes = Math.floor((claimWait % 3600) / 60);
                const seconds = claimWait % 60;
                displayText = `${hours.toString().padStart(2, '0')}:${minutes
                  .toString()
                  .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                buttonDisabled = true;
              }
            }

            return (
              <div key={`${n.template_id}-${index}`} className="chest-card-wrapper">
              <img
                className="chest-card-image"
                src={template.img}
                alt={template.nft_name || template.worker_name || ''}
              />
              {displayText && (
                <div className="nft-overlay-text">{displayText}</div>
              )}

              <div className="chest-button-group">
                {/* Heal Button - always show first with dynamic color */}
                <button
                className={`chest-button chest-heal-button ${getHealButtonColorClass(n)}`}
                onClick={async () => {
                  const healCost = await getHealCosts(n.template_id);
                  if (healCost) {
                  setSel(n); // Set the selected item for healing
                  setHealCost(healCost);
                  setHealModalOpen(true);
                  } else {
                  setErrorMessage('Unable to calculate heal cost');
                  setErrorModalOpen(true);
                  }
                }}
                disabled={isProcessing || (() => {
                  const decay = calculateRealTimeDecay(n, isWorker, currentSeason);
                  return decay.current >= decay.max || (decay.current <= 0);
                })()}
                title={(() => {
                  const decay = calculateRealTimeDecay(n, isWorker, currentSeason);
                  return decay.current >= decay.max
                  ? `${isWorker ? 'Worker' : 'Asset'} is at full health (336)`
                  : decay.current > 0 && decay.current < decay.max
                    ? `Restore decay from ${decay.current} to ${decay.max}`
                    : decay.current <= 0 ? 'Decayed 100% and cannot be healed' : "No healing needed";
                })()}
                >
                Heal
                </button>

                {/* Main Action Button - Repair for NFTs or Remove for all */}
                <button
                className={`chest-button ${!isWorker && !isFullDurability ? 'chest-repair-button' : 'chest-remove-button'}`}
                onClick={() => {
                  !isWorker && !isFullDurability
                  ? handleRepairChest(n.asset_id)
                  : handleUnstake(n.asset_id);
                }}
                disabled={isProcessing || (!isWorker && !isFullDurability && buttonDisabled)}
                style={{
                  opacity: (isProcessing || (!isWorker && !isFullDurability && buttonDisabled)) ? 0.5 : 1,
                  cursor: (isProcessing || (!isWorker && !isFullDurability && buttonDisabled)) ? 'not-allowed' : 'pointer'
                }}
                title={!isWorker && !isFullDurability ? "Repair durability" : "Remove from chest"}
                >
                {!isWorker && !isFullDurability ? "Repair" : "Remove"}
                </button>
              </div>
              </div>
            );
          })}
        </div>
      );
    }

    else if (tab === 'inventory') {
      const filteredInventory = inventory.filter(n => {
        const template =
          NFT_TEMPLATES.find(t => t.template_id === n.template_id) ||
          WORKER_TEMPLATES.find(w => w.template_id === n.template_id) ||
          PACK_TEMPLATES.find(w => w.template_id === n.template_id);

        if (!template) return false;

        const isPack = n.schema_name === 'packs';
        const matchedPackTemplate = PACK_TEMPLATES.find(p => p.template_id === n.template_id);
        if (isPack && (!matchedPackTemplate || !matchedPackTemplate.content_token)) {
          return false;
        }

        return true;
      });

      if (!filteredInventory || filteredInventory.length === 0) {
        return renderNoNftsMessage("You do not own any NFTs.\nCraft or buy NFTs to start playing!");
      }

      return (
        <div className="chest-scroll-list" ref={chestScrollRef}>
          {filteredInventory.map((n, index) => {
            const template =
              NFT_TEMPLATES.find(t => t.template_id === n.template_id) ||
              WORKER_TEMPLATES.find(w => w.template_id === n.template_id) ||
              PACK_TEMPLATES.find(w => w.template_id === n.template_id);

            const isPack = n.schema_name === 'packs';

            return (
              <div key={`${n.template_id}-${index}`} className="chest-card-wrapper">
                <img
                  className="chest-card-image"
                  src={template.img}
                  alt={template.nft_name || template.worker_name || ''}
                />
                <button
                  className="chest-unstake-button"
                  onClick={() => handleStake(n.asset_id)}
                  disabled={isProcessing}
                >
                  {isProcessing ? 'Processing' : (isPack ? 'Unpack' : 'Stake')}
                </button>
              </div>
            );
          })}
        </div>
      );
    }
    else if (tab === 'smith') {
      return (
        <div className="craft-scroll-list">
          {
            NFT_TEMPLATES.filter(template => template.type !== 'healer').map((template, index) => {
            const isSelected = selcraft?.template_id === template.template_id;
            return (
              <img
              key={`nft-${template.template_id}-${index}`}
              className={`craft-thumb ${isSelected ? 'active' : 'faded'}`}
              src={template.img}
              onClick={() => setSelcraft({ template_id: template.template_id })}
              alt={template.nft_name || ''}
              />
            );
            })}
          {WORKER_PACK_TEMPLATES.map((template, index) => {
            const isSelected = selcraft?.template_id === template.template_id;
            return (
              <img
                key={`pack-${template.template_id}-${index}`}
                className={`craft-thumb ${isSelected ? 'active' : 'faded'}`}
                src={template.img}
                onClick={() => setSelcraft({ template_id: template.template_id })}
                alt={template.pack_name || ''}
              />
            );
          })}
        </div>
      );
    }


    return null;
  };



  const renderRightPanel = () => {
    if (tab === 'home') {
      const currentSeason = SEASONS.find(s => s.id === seasonId);
      if (!sel) return null;

      const template_id = NFT_TEMPLATES.find(t => t.template_id === sel.template_id);
      const isHealer = template_id?.type === 'healer';
      
      if(isHealer){
        const formatTimestamp = (timestamp) => {
          if (!timestamp) return 'Unknown';
          const date = new Date(timestamp * 1000); 
          return date.toLocaleString();
        };

        // Get healer info
        const healAmount = NFT_TEMPLATES.find(t => t.template_id === playerHealData.template_id)?.heal_amount || 0;
        const usedCount = playerHealData?.nft_history?.length || 0;

        return (
          <div className="right-detail-panel healer-panel">
            <div className="healer-layout">
              <img 
                style={{width: 'auto', height: '18vw', marginBottom: '1vw'}}
                src={template_id.img} 
                alt={template_id.nft_name} 
              />
              <div className="healer-info-panel">
                <div className="nft-stats">
                  <h2 className="nft-title">
                    <span className="nft-title-left">
                      {template_id?.type && (
                        <img
                          className="type-icon"
                          style={{ 'border': `1px solid ${template_id?.border}`, 'borderRadius': `1vw`, 'backgroundColor': 'rgba(0,0,0,.5)' }}
                          src={template_id?.icon}
                          alt={template_id?.type}
                        />
                      )}
                      {template_id?.nft_name}
                    </span>
                  </h2>
                  
                  <div className="nft-stat-row">
                    <span className="nft-stat-label">Asset ID:</span>
                    <span className="nft-stat-value">#{playerHealData.asset_id}</span>
                  </div>
                  
                  <div className="nft-stat-row">
                    <span className="nft-stat-label">Added:</span>
                    <span className="nft-stat-value">{formatTimestamp(playerHealData?.addtime)}</span>
                  </div>
                  
                  <div className="nft-stat-row">
                    <span className="nft-stat-label">Heal Power:</span>
                    <span className="nft-stat-value healer-percentage">{healAmount}%</span>
                  </div>
                  
                  <div className="nft-stat-row">
                    <span className="nft-stat-label">Times Used:</span>
                    <span className="nft-stat-value">{usedCount}</span>
                  </div>
                  

                </div>
                
                <div className="action-button-wrapper" style={{'marginTop': '1vw'}}>
                  <div className="mine-button-group">
                    <button 
                      className="mine-button"
                      onClick={() => handleUnstake(playerHealData.asset_id)}
                      disabled={isProcessing}
                    >
                      {isProcessing ? 'Processing...' : 'Withdraw Healer'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      }

      const isWorker = WORKER_TEMPLATES.some(t => t.template_id === sel.template_id);
      if (isWorker) {
        const template = WORKER_TEMPLATES.find(t => t.template_id === sel.template_id);
        if (!template) return null;

        return (
          <div className="right-detail-panel">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <img className="nft-large" src={template.img} alt={template.worker_name} />
              <div className="action-button-wrapper">
                <div className="heal-button-group">
                  <button
                    className={`heal-button ${getHealButtonColorClass(sel)} ${isProcessing ? 'processing' : ''}`}
                    onClick={async () => {
                      const healCost = await getHealCosts(sel.template_id);
                      if (healCost) {
                        setHealCost(healCost);
                        setHealModalOpen(true);
                      } else {
                        setErrorMessage('Unable to calculate heal cost');
                        setErrorModalOpen(true);
                      }
                    }}
                    disabled={isProcessing || (() => {
                      const decay = calculateRealTimeDecay(sel, true, currentSeason);
                      return decay.current >= decay.max || (decay.current <= 0);
                    })()}
                    title={(() => {
                      const decay = calculateRealTimeDecay(sel, true, currentSeason);
                      return decay.current >= decay.max
                        ? "Worker is at full health (336)"
                        : decay.current > 0 && decay.current < decay.max
                          ? `Restore decay from ${decay.current} to ${decay.max}`
                          : "No healing needed";
                    })()}
                  >
                    {'Heal'}
                  </button>
                </div>
              </div>
            </div>
            <div className="nft-stats">
              <h2 className="nft-title">
                <span className="nft-title-left">
                  {template?.type && (
                    <img
                      className="type-icon"
                      src={TYPE_ICONS[template.type]}
                      alt={template.type}
                    />
                  )}
                  {template?.worker_name}
                </span>
              </h2>
              <div className="nft-stat-row">
                <span className="nft-stat-label">Extra Slots:</span>
                <span className="nft-stat-value">{template.additional_slots}</span>
              </div>
              <div className="nft-stat-row">
                <span className="nft-stat-label">Extra Stamina:</span>
                <span className="nft-stat-value">{template.additional_stamina}</span>
              </div>
              <div className="nft-stat-row">
                <span className="nft-stat-label">Decay Time:</span>
                <span className="nft-stat-value">
                  {(() => {
                    const decay = calculateRealTimeDecay(sel, true, currentSeason);
                    return `(${decay.current}/${decay.max} hrs)${decay.hoursDecayed > 0 ? ` [-${decay.hoursDecayed}h]` : ''}`;
                  })()}
                </span>
              </div>
              {isHealerAvailable() && (() => {
                const healerEffect = calculateHealerEffect(sel, playerHealData);
                return healerEffect?.canUse ? (
                  <div className="nft-stat-row">
                    <span className="nft-stat-label">After Healing:</span>
                    <span className="nft-stat-value healer-improvement">{healerEffect.healedDecay}/336 hrs (+{healerEffect.improvement}h)</span>
                  </div>
                ) : null;
              })()}
              <div className="nft-stat-row">
                <span className="nft-stat-label">Stored Mining:</span>
                <span className="nft-stat-value">{template.saved_claims}</span>
              </div>
              <div className="nft-stat-row">
                <span className="nft-stat-label">Reward Rate:</span>
                <span className="nft-stat-value">{template.rewards_rate?.[0]}</span>
              </div>
              <div className="nft-stat-row">
                <span className="nft-stat-label">Stamina Consumed:</span>
                <span className="nft-stat-value">{template.stamina}</span>
              </div>
              <div className="nft-stat-row">
                <span className="nft-stat-label">Charge Time:</span>
                <span className="nft-stat-value">
                  {(() => {
                    const time = currentSeason
                      ? (template?.charged_time * currentSeason.cooldown_multiplier) / 100
                      : template?.charged_time;

                    const hours = Math.floor(time / 3600);
                    const minutes = Math.floor((time % 3600) / 60);
                    return `${hours > 0 ? `${hours}h` : ''}${minutes > 0 ? `${minutes}min` : ''}`;
                  })()}
                </span>
              </div>

              <div className="timer-box">
                {remainingTime > 0
                  ? `${String(Math.floor(remainingTime / 3600)).padStart(2, '0')}:${String(Math.floor((remainingTime % 3600) / 60)).padStart(2, '0')}:${String(remainingTime % 60).padStart(2, '0')}`
                  : 'Ready to claim!'}
              </div>

              <div className="action-button-wrapper">
                <div className="mine-button-group">
                  <button
                    className={`mine-button ${isProcessing ? 'processing' : ''}`}
                    onClick={handleClaimWorker}
                    disabled={isProcessing || remainingTime > 0 || template.stamina > (stamina?.first || 0)}
                  >
                    {isProcessing ? 'Claiming...' : 'Claim'}
                  </button>
                </div>
                {(remainingTime === 0 && template.stamina > (stamina?.first || 0)) && (
                  <span className="action-warning">Low Stamina</span>
                )}
              </div>
            </div>
          </div>
        );
      }

      const template = NFT_TEMPLATES.find(t => t.template_id === sel.template_id);
      const maxDurability = sel.durability?.second || 100;
      const currentDurability = sel.durability?.first || 0;
      const baseTime = template?.charged_time || 3600;
      const chargedTime = currentSeason ? (baseTime * currentSeason.cooldown_multiplier) / 100 : baseTime;
      const now = Math.floor(Date.now() / 1000);

      let isRepairDisable = false;
      if (template) {
        const toolCost = ((maxDurability - currentDurability) * currentSeason.durability_cost) / 100;
        const userTool = parseFloat(balances?.[2] || 0);
        isRepairDisable = userTool < toolCost;
      }
      // Aynı tipe sahip tüm worker'ları bul
      const matchedWorkers = workers.filter(w => {
        const wTemp = WORKER_TEMPLATES.find(t => t.template_id === w.template_id);
        return wTemp?.type === template?.type;
      });

      // Tüm worker'ların saved_claims toplamı
      const extraClaims = matchedWorkers.reduce((sum, w) => {
        const workerTemplate = WORKER_TEMPLATES.find(t => t.template_id === w.template_id);
        return sum + (workerTemplate?.saved_claims || 0);
      }, 0);

      // Max hak = 1 + extra
      const totalClaimable = 1 + extraClaims;
      let current = 0;
      let next = sel.next_claim || 0;

      if (now >= next) {
        const diff = now - next;
        const fullCharges = Math.floor(diff / chargedTime);
        current = Math.min(1 + fullCharges, totalClaimable);
      }

      const remaining = current >= totalClaimable ? 0 : (next + current * chargedTime - now);

      // ✅ Calculate decay information for heal button - now with real-time calculation
      const decayInfo = calculateRealTimeDecay(sel, false, currentSeason);
      const isDecayFull = decayInfo.current >= decayInfo.max; // 336 is full
      const needsHealing = decayInfo.current > 0 && decayInfo.current < decayInfo.max;

      return (
        <div className="right-detail-panel">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img className="nft-large" src={template?.img} alt={template?.nft_name} />
            {/* Always render durability bar for NFT tools */}
            {renderDurabilityBar(currentDurability, maxDurability)}
            <div className="action-button-wrapper" style={{ marginTop: '.8rem' }}>
              <div className="heal-button-group">
                <button
                  className={`heal-button ${getHealButtonColorClass(sel)} ${isProcessing ? 'processing' : ''}`}
                  onClick={async () => {
                    const healCost = await getHealCosts(sel.template_id);
                    if (healCost) {
                      setHealCost(healCost);
                      setHealModalOpen(true);
                    } else {
                      setErrorMessage('Unable to calculate heal cost');
                      setErrorModalOpen(true);
                    }
                  }}
                  disabled={isProcessing || (() => {
                    const decay = calculateRealTimeDecay(sel, false, currentSeason);
                    return decay.current >= decay.max || (decay.current <= 0);
                  })()}
                  title={(() => {
                    const decay = calculateRealTimeDecay(sel, false, currentSeason);
                    return decay.current >= decay.max
                      ? "Asset is at full health (336)"
                      : decay.current > 0 && decay.current < decay.max
                        ? `Restore decay from ${decay.current} to ${decay.max}`
                        : "No healing needed";
                  })()}
                >
                  {'Heal'}
                </button>
              </div>
            </div>
          </div>
          <div className="nft-stats">
            <h2 className="nft-title">
              <span className="nft-title-left">
                {template?.type && (
                  <img
                    className="type-icon"
                    src={TYPE_ICONS[template.type] || ''}
                    alt={template.type}
                  />
                )}
                {template?.nft_name}
              </span>
              <span className="nft-status-box">{current}/{totalClaimable}</span>
            </h2>


            <div className="nft-stat-row"><span className="nft-stat-label">Rarity:</span><span className="nft-stat-value">{template?.rarity}</span></div>
            <div className="nft-stat-row"><span className="nft-stat-label">Reward Rate:</span><span className="nft-stat-value">{template?.rewards}</span></div>
            <div className="nft-stat-row">
              <span className="nft-stat-label">Decay Time:</span>
              <span className="nft-stat-value">
                {(() => {
                  const decay = calculateRealTimeDecay(sel, false, currentSeason);
                  return `(${decay.current}/${decay.max}) hrs${decay.hoursDecayed > 0 ? ` [-${decay.hoursDecayed}h]` : ''}`;
                })()}
              </span>
            </div>
            {isHealerAvailable() && (() => {
              const healerEffect = calculateHealerEffect(sel, playerHealData);
              return healerEffect?.canUse ? (
                <div className="nft-stat-row">
                  <span className="nft-stat-label">After Healing:</span>
                  <span className="nft-stat-value healer-improvement">{healerEffect.healedDecay}/336 hrs (+{healerEffect.improvement}h)</span>
                </div>
              ) : null;
            })()}
            <div className="nft-stat-row">
              <span className="nft-stat-label">Charge Time:</span>
              <span className="nft-stat-value">
                {(() => {
                  const baseTime = template?.charged_time || 0;
                  const time = currentSeason ? (baseTime * currentSeason.cooldown_multiplier) / 100 : baseTime;
                  const h = Math.floor(time / 3600);
                  const m = Math.floor((time % 3600) / 60);
                  return `${h > 0 ? `${h}h` : ''}${m > 0 ? `${m}min` : ''}` || '0min';
                })()}
              </span>
            </div>
            <div className="nft-stat-row"><span className="nft-stat-label">Stamina Consumed:</span><span className="nft-stat-value">{template?.consumed.first}</span></div>
            <div className="nft-stat-row"><span className="nft-stat-label">Durability Consumed:</span><span className="nft-stat-value">{template?.consumed.second}</span></div>

            <div className="timer-box">
              {remainingTime > 0
                ? `${String(Math.floor(remainingTime / 3600)).padStart(2, '0')}:${String(Math.floor((remainingTime % 3600) / 60)).padStart(2, '0')}:${String(remainingTime % 60).padStart(2, '0')}`
                : 'Ready to claim!'}
            </div>

            <div className="action-buttons">
              <div className="action-button-wrapper">
                <div className="mine-button-group">
                  <button
                    className={`mine-button ${isProcessing ? 'processing' : ''}`}
                    onClick={handleMine}
                    disabled={isProcessing || current === 0 || (template?.consumed?.first || 0) * current > (stamina?.first || 0)}
                  >
                    {'Mine'}
                  </button>
                </div>
                {(current !== 0 && (template?.consumed?.first || 0) * current > (stamina?.first || 0)) && (
                  <span className="action-warning">Low Stamina</span>
                )}
              </div>

              <div className="action-button-wrapper">
                <div className="repair-button-group">
                  <button
                    className={`repair-button ${isProcessing ? 'processing' : ''}`}
                    onClick={handleRepair}
                    disabled={isProcessing || currentDurability >= maxDurability || isRepairDisable}
                  >
                    {'Repair'}
                  </button>

                  <button
                    className="repair-info-button"
                    onClick={() => onOpenRepairModal(sel)}
                    disabled={!sel}
                    title="Show repair cost info"
                  >
                    <img src={questionIcon} alt="info" className="repair-info-icon" />
                  </button>
                </div>

                {(currentDurability < maxDurability && isRepairDisable) && (
                  <span className="action-warning">Low TOOL</span>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
    else if (tab === 'smith') {
      const template =
        NFT_TEMPLATES.find(t => t.template_id === selcraft?.template_id && t.type !== 'healer') ||
        WORKER_PACK_TEMPLATES.find(p => p.template_id === selcraft?.template_id); // sadece NFT ve Worker Pack kullanıyoruz

      if (!template) return null;

      const isWorkerPack = WORKER_PACK_TEMPLATES.some(p => p.template_id === selcraft?.template_id);
      let workerStats = null;
      let isCraftDisabled = false;
      let errormessage = "";
      if (template) {
        if (isWorkerPack) {
          const toolCost = parseFloat(template.mint_cost.first);
          const rcoinCost = parseFloat(template.mint_cost.second);
          const userTool = parseFloat(balances?.[2] || 0);
          const userRcoin = parseFloat(balances?.[3] || 0);

          isCraftDisabled = userTool < toolCost || userRcoin < rcoinCost || stamina.first - 200 < template.mint_consume;
          if (stamina.first - 200 < template.mint_consume)
            errormessage = "low Stamina";

          else if (userRcoin < rcoinCost)
            errormessage = "low RCOIN";
          else
            errormessage = "low TOOL";
        } else {
          const toolCost = parseFloat(template.mints?.[1] || 0);
          const ranchCost = parseFloat(template.mints?.[0] || 0);
          const userTool = parseFloat(balances?.[2] || 0);
          const userRanch = parseFloat(balances?.[0] || 0);

          isCraftDisabled = userTool < toolCost || userRanch < ranchCost || stamina.first - 200 < template.mint_consume;
          if (stamina.first - 200 < template.mint_consume)
            errormessage = "low Stamina";
          else if (userRanch < ranchCost)
            errormessage = "low RANCH";
          else
            errormessage = "low TOOL";
        }
      }
      // Eğer worker pack ise ilgili worker template'ı bul
      if (isWorkerPack) {
        const baseWorkerName = template.pack_name.replace(' Pack', ''); // "Bronze Worker" gibi
        workerStats = WORKER_TEMPLATES.find(w => w.worker_name === baseWorkerName);
      }

      return (
        <div className="craft-detail-panel">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <img
              className="craft-large"
              src={template.img}
              alt={template.nft_name || template.template_id || template.template_id}
            />
          </div>

          <div className="craft-stats">
            <h2 className="craft-name">
              {template?.type && (
                <img
                  className="craft-type-icon"
                  src={TYPE_ICONS[template.type] || ''}
                  alt={template.type}
                />
              )}
              {template.nft_name || template.worker_name || template.pack_name}
            </h2>

            {/* Eğer normal NFT ise */}
            {!isWorkerPack && (
              <>
                <div className="nft-stat-row">
                  <span className="nft-stat-label">Rarity:</span>
                  <span className="nft-stat-value">{template?.rarity || '-'}</span>
                </div>
                <div className="nft-stat-row">
                  <span className="nft-stat-label">Base Reward Rate:</span>
                  <span className="nft-stat-value">{template?.rewards?.[0] || '-'}</span>
                </div>
                <div className="nft-stat-row">
                  <span className="nft-stat-label">Base Charge Time:</span>
                  <span className="nft-stat-value">{template?.charged_time ? (template.charged_time / 3600) + ' hours' : '-'}</span>
                </div>
                {template.consumed && (
                  <>
                    <div className="nft-stat-row">
                      <span className="nft-stat-label">Stamina Consumed:</span>
                      <span className="nft-stat-value">{template.consumed.first}</span>
                    </div>
                    <div className="nft-stat-row">
                      <span className="nft-stat-label">Durability Consumed:</span>
                      <span className="nft-stat-value">{template.consumed.second}</span>
                    </div>
                  </>
                )}
                <div className="nft-stat-row2">
                  <span className="nft-stat-value">Reserve Stamina (200) cannot be used for crafting.</span>
                </div>
              </>
            )}

            {/* Eğer Worker Pack ise, worker bilgilerini göster */}
            {isWorkerPack && workerStats && (
              <>
                <div className="nft-stat-row">
                  <span className="nft-stat-label">Saved Claims:</span>
                  <span className="nft-stat-value">{workerStats.saved_claims}</span>
                </div>
                <div className="nft-stat-row">
                  <span className="nft-stat-label">Additional Slots:</span>
                  <span className="nft-stat-value">{workerStats.additional_slots}</span>
                </div>
                <div className="nft-stat-row">
                  <span className="nft-stat-label">Additional Stamina:</span>
                  <span className="nft-stat-value">{workerStats.additional_stamina}</span>
                </div>
                <div className="nft-stat-row">
                  <span className="nft-stat-label">Lucky:</span>
                  <span className="nft-stat-value">{workerStats.lucky}</span>
                </div>
                <div className="nft-stat-row">
                  <span className="nft-stat-label">Base Charge Time:</span>
                  <span className="nft-stat-value">{workerStats.charged_time ? (workerStats.charged_time / 3600) + ' hours' : '-'}</span>
                </div>
                <div className="nft-stat-row2">
                  <span className="nft-stat-value">Reserve Stamina (200) cannot be used for crafting.</span>
                </div>
              </>
            )}

            {/* Cost bilgisi */}
            {isWorkerPack ? (
              <div className="craft-cost-wrapper">
                {template.mint_cost && (
                  <>
                    <div className="cost-item">
                      <img className="cost-icon" src={TOKEN_ICONS['RCOIN']} alt="RCOIN" />
                      <span className="cost-value">{parseFloat(template.mint_cost.second)}</span>
                    </div>
                    <div className="cost-item">
                      <img className="cost-icon" src={TOKEN_ICONS['TOOL']} alt="TOOL" />
                      <span className="cost-value">{parseFloat(template.mint_cost.first)}</span>
                    </div>
                  </>
                )}
                {template.mint_consume > 0 && (
                  <div className="cost-item">
                    <img className="cost-icon" src={TOKEN_ICONS['STAMINA']} alt="STAMINA" />
                    <span className="cost-value">{parseFloat(template.mint_consume)}</span>
                  </div>
                )}
              </div>
            ) : (
              template.mints && template.mints.length > 0 && (
                <div className="craft-cost-wrapper">
                  {template.mints.map((mint, index) => {
                    const [amount, token] = mint.split(' ');
                    return (
                      <div key={index} className="cost-item">
                        <img
                          className="cost-icon"
                          src={TOKEN_ICONS[token.toUpperCase()]}
                          alt={token}
                        />
                        <span className="cost-value">{parseFloat(amount)}</span>
                      </div>
                    );
                  })}
                  {template.mint_consume > 0 && (
                    <div className="cost-item">
                      <img className="cost-icon" src={TOKEN_ICONS['STAMINA']} alt="STAMINA" />
                      <span className="cost-value">{parseFloat(template.mint_consume)}</span>
                    </div>
                  )}
                </div>
              )
            )}

            <div className="craft-button-wrapper">
              <button className={`craft-button ${isProcessing ? 'processing' : ''}`}
                onClick={handleCraft}
                disabled={isProcessing || isCraftDisabled}
              >
                {isProcessing ? 'Crafting...' : 'Craft'}
              </button>
              {(isCraftDisabled) && (
                <span className="action-warning">{errormessage}</span>
              )}
            </div>
          </div>
        </div>
      );
    }
    else if (tab === 'exchange') {
      return (
        <div className="exchange-panel">
          <div className="exchange-tabs">
            {exchangeTab === 'withdraw' && (
              <div className="exchange-fee">Fee: {fee}%</div>
            )}
            <button className={exchangeTab === 'withdraw' ? 'active' : ''} onClick={() => setExchangeTab('withdraw')}>
              Withdraw
            </button>
            <button className={exchangeTab === 'deposit' ? 'active' : ''} onClick={() => setExchangeTab('deposit')}>
              Deposit
            </button>
          </div>

          <div className="exchange-content">
            <div className="exchange-section">
              <h3>Balances</h3>
              <div className="balances-row">
                {exchangeTab === 'withdraw' ? (
                  <>
                    <div><span className="amount">{formatNumber(parseFloat(balances[0]) || 0)}</span> <span className="token">RANCH</span></div>
                    <div><span className="amount">{formatNumber(parseFloat(balances[1]) || 0)}</span> <span className="token">FARM</span></div>
                    <div><span className="amount">{formatNumber(parseFloat(balances[2]) || 0)}</span> <span className="token">TOOL</span></div>
                    <div><span className="amount">{formatNumber(parseFloat(balances[3]) || 0)}</span> <span className="token">RCOIN</span></div>
                  </>
                ) : (
                  <>
                    <div><span className="amount">{formatNumber(parseFloat(tokenBalances[0]) || 0)}</span> <span className="token">RANCH</span></div>
                    <div><span className="amount">{formatNumber(parseFloat(tokenBalances[1]) || 0)}</span> <span className="token">FARM</span></div>
                    <div><span className="amount">{formatNumber(parseFloat(tokenBalances[2]) || 0)}</span> <span className="token">TOOL</span></div>
                    <div><span className="amount">{formatNumber(parseFloat(tokenBalances[3]) || 0)}</span> <span className="token">RCOIN</span></div>
                  </>
                )}
              </div>
            </div>

            <div className="exchange-section">
              <h3>{exchangeTab === 'withdraw' ? 'Withdrawal Amount' : 'Deposit Amount'}</h3>
              <div className="input-row">
                <div className="exchange-input-wrapper">
                  <img src={ranchIcon} alt="RANCH" className="exchange-input-icon" />
                  <input
                    ref={ranchInputRef}
                    className="exchange-input"
                    placeholder="RANCH"
                    onInput={(e) => handleInput(e, parseFloat((exchangeTab === 'withdraw' ? balances[0] : tokenBalances[0]) || 0), 'ranch')}
                    onKeyDown={handleKeyDown}
                    onBlur={() => handleBlur('ranch')}
                  />
                </div>
                <div className="exchange-input-wrapper">
                  <img src={farmIcon} alt="FARM" className="exchange-input-icon" />
                  <input
                    ref={farmInputRef}
                    className="exchange-input"
                    placeholder="FARM"
                    onInput={(e) => handleInput(e, parseFloat((exchangeTab === 'withdraw' ? balances[1] : tokenBalances[1]) || 0), 'farm')}
                    onKeyDown={handleKeyDown}
                    onBlur={() => handleBlur('farm')}
                  />

                </div>
                <div className="exchange-input-wrapper">
                  <img src={toolIcon} alt="TOOL" className="exchange-input-icon" />
                  <input
                    ref={toolInputRef}
                    className="exchange-input"
                    placeholder="TOOL"
                    onInput={(e) => handleInput(e, parseFloat((exchangeTab === 'withdraw' ? balances[2] : tokenBalances[2]) || 0), 'tool')}
                    onKeyDown={handleKeyDown}
                    onBlur={() => handleBlur('tool')}
                  />

                </div>
                <div className="exchange-input-wrapper">
                  <img src={rcoinIcon} alt="RCOIN" className="exchange-input-icon" />
                  <input
                    ref={rcoinInputRef}
                    className="exchange-input"
                    placeholder="RCOIN"
                    onInput={(e) => handleInput(e, parseFloat((exchangeTab === 'withdraw' ? balances[3] : tokenBalances[3]) || 0), 'rcoin')}
                    onKeyDown={handleKeyDown}
                    onBlur={() => handleBlur('rcoin')}
                  />
                </div>
              </div>

            </div>

            <div className="exchange-section">
              {exchangeTab === 'withdraw' && (
                <div className="exchange-section">
                  <h3>Total Token</h3>
                  <div className="total-row">
                    {exchangeTab === 'withdraw' ? (
                      <>
                        <div>
                          <span className="amount">{formatNumber((parseFloat(inputValues.ranch || 0) * (1 - fee / 100)) || 0)}</span>
                          <span className="token">RANCH</span>
                        </div>
                        <div>
                          <span className="amount">{formatNumber((parseFloat(inputValues.farm || 0) * (1 - fee / 100)) || 0)}</span>
                          <span className="token">FARM</span>
                        </div>
                        <div>
                          <span className="amount">{formatNumber((parseFloat(inputValues.tool || 0) * (1 - fee / 100)) || 0)}</span>
                          <span className="token">TOOL</span>
                        </div>
                        <div>
                          <span className="amount">{formatNumber((parseFloat(inputValues.rcoin || 0) * (1 - fee / 100)) || 0)}</span>
                          <span className="token">RCOIN</span>
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              )}

            </div>
          </div>
          <div className="exchange-bottom-button">
            <button
              className="exchange-action-button"
              onClick={exchangeTab === 'withdraw' ? handleWithdraw : handleDeposit}
              disabled={
                isProcessing || (
                  (exchangeTab === 'withdraw' || exchangeTab === 'deposit') &&
                  parseFloat(inputValues.ranch || 0) <= 0 &&
                  parseFloat(inputValues.farm || 0) <= 0 &&
                  parseFloat(inputValues.tool || 0) <= 0 &&
                  parseFloat(inputValues.rcoin || 0) <= 0
                )
              }
            >
              {isProcessing
                ? "Processing"
                : (exchangeTab === "withdraw" ? "Withdraw" : "Deposit")}
            </button>
          </div>
        </div>
      );
    }
    else if (tab === 'network') {
      const acct = currentNetworkUser || wallet;

      return (
        <div className="right-detail-panel network-panel">
          {currentNetworkUser === null ? (
            <>
              <div className="network-section">
                <h3>My Referrer</h3>
                {networkData.referrer
                  ? <div className="network-item">{networkData.referrer}</div>
                  : <div className="network-empty">No referrer linked. You’re playing independently.</div>
                }
              </div>
              <div className="network-section">
                <h3>Invited Friends</h3>
                {networkData.invites.length > 0
                  ? <div className="network-list">
                    {networkData.invites.map(inv => (
                      <button
                        key={inv}
                        className="network-item clickable"
                        onClick={() => handleInviteClick(inv)}
                      >
                        {inv}
                      </button>
                    ))}
                  </div>
                  : <div className="network-empty">You haven’t invited anyone yet.</div>
                }
              </div>
            </>
          ) : (
            <>
              <div className="network-header-line">
                {networkStack.length > 1 && (
                  <button className="network-back" onClick={handleBack}>
                    Back
                  </button>
                )}
              </div>
              {networkEarnings.length > 0 && (
                <div className="network-section">
                  <h3>Earned for {acct}</h3>
                  <div className="earnings-box-group">
                    {networkEarnings.map(({ token, amount }) => (
                      <div key={token} className="earnings-box">
                        <div className="earnings-value">
                          {amount.toLocaleString(undefined, { minimumFractionDigits: 4 })}
                        </div>
                        <img src={TOKEN_ICONS[token]} alt={token} className="earnings-icon" />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="network-section">
                <h3>{acct} invited</h3>
                {networkData.invites.length > 0
                  ? <div className="network-list">
                    {networkData.invites.map(inv => (
                      <button
                        key={inv}
                        className="network-item clickable"
                        onClick={() => handleInviteClick(inv)}
                      >
                        {inv}
                      </button>
                    ))}
                  </div>
                  : <div className="network-empty">{acct} hasn’t invited anyone.</div>
                }
              </div>
            </>
          )}
        </div>
      );


    }



    else if (tab === 'settings') {
      return (
        <div className="settings-panel">
          <div className="settings-group">
            <label>Invite Link:</label>
            <span className="referral-text">
              https://www.ranchers.land/play/?ref={wallet}
            </span>
            <div className="referral-button-wrapper">
              <button onClick={handleCopyReferral} className="referral-copy-button">
                Copy
              </button>
              {copyStatus === 'Copied!' && (
                <span className="copied-indicator">Copied!</span>
              )}
            </div>
          </div>
          <div className="settings-group">
            <label htmlFor="referrer">My Referrer:</label>
            <input
              id="referrer"
              type="text"
              value={hasReferrer ? referrerAccount : refInput}
              onChange={(e) => setRefInput(e.target.value.toLowerCase().replace(/\s/g, ''))}
              onKeyDown={(e) => {
                if (e.key === ' ') e.preventDefault();
              }}
              maxLength={12}
              placeholder="wax account"
              disabled={hasReferrer}
              readOnly={hasReferrer}
            />
            {!hasReferrer && (
              <button onClick={handleSetReferrer} disabled={isProcessing}>
                Set
              </button>
            )}
          </div>
          <div className="settings-group">
            <label htmlFor="assistance">Assistance:</label>
            <input
              id="assistance"
              type="text"
              value={assistanceInput}
              onChange={(e) => setAssistanceInput(e.target.value.toLowerCase().replace(/\s/g, ''))}
              onKeyDown={(e) => {
                if (e.key === ' ') e.preventDefault();
              }}
              maxLength={12}
              placeholder="wax account"
              disabled={!!assistance}
              readOnly={!!assistance}
            />
            {assistance === '' ? (
              <button onClick={handleSetAssistance} disabled={isProcessing}>
                Set
              </button>
            ) : (
              <button onClick={handleDeleteAssistance} disabled={isProcessing}>
                Delete
              </button>
            )}
          </div>
          <div className="settings-group">
            <label>Support Us:</label>
            <div className="proxy-button-group">
              <button onClick={handleVoteProxy} disabled={isProcessing}>
                Vote Proxy
              </button>
              <button
                onClick={() => window.open('https://ranchers-land.gitbook.io/ranchersland/vote-proxy', '_blank')}
                className="proxy-info-button"
                title="What is Vote Proxy?"
              >
                <img src={questionIcon} alt="info" className="proxy-info-icon" />
              </button>
            </div>
          </div>



        </div>
      );

    }

    return null;
  };
  //<button onClick={handletestact}>Test</button>
  const loggedInUser = localStorage.getItem('loggedInUser'); // ekle üst kısımlara
  
  const buttons = [
    { key: 'home', icon: homeIcon, label: 'HOME' },

    ...(loggedInUser === wallet ? [
      { key: 'chest', icon: chestIcon, label: 'CHEST' },
      { key: 'inventory', icon: inventoryIcon, label: 'STASH' },
      { key: 'smith', icon: smithIcon, label: 'CRAFT' },
      { key: 'exchange', icon: exchangeIcon, label: 'VAULT' },
      { key: 'network', icon: networkIcon, label: 'NETWORK' },
      { key: 'settings', icon: settingIcon, label: 'SETTINGS' },
    ] : []),
  ];
  const isValidEosioName = (name) => /^[a-z1-5.]{1,12}$/.test(name);

  const [assistanceInput, setAssistanceInput] = useState('');
  useEffect(() => {
    if (tab === 'settings') {
      setAssistanceInput(assistance || '');
    }
  }, [tab, assistance]);

  const handleSetAssistance = async () => {

    if (!isValidEosioName(assistanceInput)) {
      setErrorMessage('Invalid WAX name. Must be 1-12 chars, a-z, 1-5 only.');
      setErrorModalOpen(true);
      return;
    }
    else {
      const tx = await actSetAssistance(assistanceInput);

      if (tx?.error) {
        setErrorMessage(tx.error);
        setErrorModalOpen(true);
        return;
      }

      if (tx?.log) {
        setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
        setLogModalOpen(true);
      }
      if (onRefresh) onRefresh();
    }
  };

  const handleSetReferrer = async () => {
    // EOSIO account name formatını kontrol et
    if (!isValidEosioName(refInput)) {
      setErrorMessage('Invalid WAX name. Must be 1-12 chars, a-z, 1-5 only.');
      setErrorModalOpen(true);
      return;
    }

    setIsProcessing(true);
    try {
      const tx = await actSetMyref(refInput);
      if (tx?.error) {
        setErrorMessage(tx.error);
        setErrorModalOpen(true);
        return;
      }

      if (tx?.log) {
        setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
        setLogModalOpen(true);
      }

      setHasReferrer(true);
      setReferrerAccount(refInput);
      if (onRefresh) onRefresh();
    } catch (err) {
      setErrorMessage(err.message || 'Unknown error setting referrer.');
      setErrorModalOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };


  const handleDeleteAssistance = async () => {
    const viewer = localStorage.getItem("loggedInUser"); // aksiyonu atan kişi

    if (!viewer) return;

    const tx = await actSetAssistance("");

    if (tx?.error) {
      setErrorMessage(tx.error);
      setErrorModalOpen(true);
      return;
    }

    if (tx?.log) {
      setLogLines(Array.isArray(tx.log) ? tx.log : [tx.log]);
      setLogModalOpen(true);
    }
    if (onRefresh) onRefresh();
  };
  return (
    <div className="canvas-center-wrapper">
      <div className="tab-label-banner">
        {buttons.find(b => b.key === tab)?.label || ''}
      </div>
      <div className='tab-label-banner2'>
        <button 
          className="alerts-button"
          onClick={handleNotificationClick}
          title="View Notifications"
        >
          <span className="alerts-text">ALERTS</span>
          {hasUnreadNotifications && <div className="notification-bell-badge"></div>}
        </button>
      </div>
      <div className='tab-label-banner3'>
        {isHealerAvailable() && (
          <div className="healer-indicator" title="Healer NFT is staked" style={{
            display: 'flex',
            alignItems: 'center', 
            justifyContent: 'center',
            width: '35px',
            height: '35px',
            background: 'rgba(0, 0, 0, 0.6)',
            borderRadius: '50%',
            color: `${NFT_TEMPLATES.find(t => t.template_id === playerHealData.template_id)?.border}`,
            fontSize: '20px',
            fontWeight: 'bold',
            border: `2px dotted ${NFT_TEMPLATES.find(t => t.template_id === playerHealData.template_id)?.border}`
          }}
            onClick={() => setSel(playerHealData)}
          >
            +
          </div>
        )}
      </div>
      <div
        className="center-panel"

      >
        <div className="top-content">
          {renderLeftList()}
          {['home', 'smith', 'exchange', 'network', 'settings'].includes(tab) && renderRightPanel()}
        </div>
      </div>

      <div
        className="bottom-menu-wrapper"
      >
        <div className="bottom-tabs">
          {buttons.map(b => (
            <button
              key={b.key}
              className={tab === b.key ? 'active' : ''}
              onClick={() => setTab(b.key)}
            >
              <img
                src={b.icon}
                alt=""
              />
              <span>{b.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ✅ Heal Modal */}
      <HealModal
        isOpen={healModalOpen}
        onClose={handleHealModalClose}
        healCost={healCost}
        onConfirm={handleHealConfirm}
        isProcessing={isProcessing}
      />
    </div>

  );
}
